/**
 * CommunityHealthTab — Community "Health" tab (Phase 1A)
 *
 * Shows community health metrics assembled from existing in-memory stores.
 * Architecture uses a sections array pattern so future phases (constitutional
 * profile, pattern detection, external data) can add sections without rewriting.
 *
 * Sections (Phase 1A):
 *   - Activity Summary (project counts, member count, workspace count)
 *   - Leadership (enabler role slots with linked users)
 *   - Open Roles (unfilled enabler slots)
 *   - Active Problem Workspaces (placeholder for workspace list)
 */

import React, { useMemo, useEffect, useState } from 'react';
import {
  Users, FolderOpen, CheckCircle, FileEdit, UserCheck, Briefcase,
  Activity, Shield, CircleDashed, Heart,
} from 'lucide-react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import UserAvatar from '../UserAvatar/UserAvatar';
import styles from './CommunityHealthTab.module.css';
import {
  communityMembershipStore,
  communityRoleStore,
  projectConfigStore,
} from '../../Data/store';
import {
  getEnablersForCommunity,
} from '../../Data/CommunityConfig/communityEnablers';
import type { CommunityEnabler } from '../../Data/CommunityConfig/communityEnablers';
import { fetchCommunityOpenRoles, assignUserToRole, updateRoleAssignment } from '../../api/projectRoles';
import type { ProjectRoleSlotResponse } from '../../api/projectRoles';

interface Props {
  communityGuid: string;
  currentUserGuid: string;
}

/* ------------------------------------------------------------------ */
/*  Activity Summary helpers                                           */
/* ------------------------------------------------------------------ */

interface ActivityStats {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  draftProjects: number;
  memberCount: number;
  roleCount: number;
}

function computeActivityStats(communityGuid: string): ActivityStats {
  const cg = communityGuid.toUpperCase();

  // Count projects linked to this community
  let totalProjects = 0;
  let activeProjects = 0;
  let completedProjects = 0;
  let draftProjects = 0;

  projectConfigStore.forEach((proj) => {
    const projCommunity = String(proj.primary_location_entity_guid || proj.community_entity_guid || '').toUpperCase();
    if (projCommunity !== cg) return;
    totalProjects++;
    const status = String(proj.status || proj.project_status || '').toLowerCase();
    if (status === 'active' || status === 'in_progress') activeProjects++;
    else if (status === 'complete' || status === 'completed') completedProjects++;
    else if (status === 'draft') draftProjects++;
  });

  // Count active members
  let memberCount = 0;
  for (const m of communityMembershipStore) {
    if (
      String(m.community_entity_guid || '').toUpperCase() === cg &&
      String(m.status || 'active').toLowerCase() === 'active'
    ) {
      memberCount++;
    }
  }

  // Count active roles
  let roleCount = 0;
  for (const r of communityRoleStore) {
    if (String(r.community_entity_guid || '').toUpperCase() === cg) {
      roleCount++;
    }
  }

  return { totalProjects, activeProjects, completedProjects, draftProjects, memberCount, roleCount };
}

/* ------------------------------------------------------------------ */
/*  Stat Card                                                          */
/* ------------------------------------------------------------------ */

function StatCard({ label, value, accent, icon }: { label: string; value: number | string; accent?: boolean; icon?: React.ReactNode }) {
  return (
    <div className={`${styles.statCard} ${accent ? styles.statCardAccent : ''}`}>
      {icon && <div className={styles.statIcon}>{icon}</div>}
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Leadership Section                                                 */
/* ------------------------------------------------------------------ */

function LeadershipSection({ enablers, t }: { enablers: CommunityEnabler[]; t: (key: string) => string }) {
  const filled = enablers.filter((e) => e.linked_user_guid || e.manual_name);
  if (filled.length === 0) return null;

  return (
    <section className={styles.sectionCard}>
      <h3 className={styles.sectionTitle}>
        <Shield size={15} />
        {t('communityDetail.health.leadership')}
        <span className={styles.sectionCount}>{filled.length}</span>
      </h3>
      <div className={styles.leaderGrid}>
        {filled.map((e) => {
          const name = e.manual_name || '(linked user)';
          const title = (e.display_title || '').trim() || 'Team Member';
          const subtitle = e.manual_title || '';
          return (
            <div key={e.role_slot_guid} className={styles.leaderCard}>
              <UserAvatar
                userGuid={e.linked_user_guid || e.role_slot_guid}
                avatarUrl={e.manual_photo_url || undefined}
                userName={name}
                size="md"
                showPresence={!!e.linked_user_guid}
              />
              <div className={styles.leaderInfo}>
                <div className={styles.leaderName}>{name}</div>
                <div className={styles.leaderRole}>{title}</div>
                {subtitle && <div className={styles.leaderSubtitle}>{subtitle}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Open Roles Section                                                 */
/* ------------------------------------------------------------------ */

function OpenRolesSection({ enablers, t }: { enablers: CommunityEnabler[]; t: (key: string) => string }) {
  const vacant = enablers.filter((e) => !e.linked_user_guid && !e.manual_name);
  if (vacant.length === 0) return null;

  return (
    <section className={styles.sectionCard}>
      <h3 className={styles.sectionTitle}>
        <CircleDashed size={15} />
        {t('communityDetail.health.openRoles')}
        <span className={styles.sectionCount}>{vacant.length}</span>
      </h3>
      <p className={styles.sectionHint}>{t('communityDetail.health.openRolesHint')}</p>
      <div className={styles.roleList}>
        {vacant.map((e) => {
          const title = (e.display_title || '').trim() || 'Team Member';
          return (
            <div key={e.role_slot_guid} className={styles.roleChip}>
              <CircleDashed size={12} />
              <span>{title}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Volunteer Opportunities (project open roles)                       */
/* ------------------------------------------------------------------ */

function VolunteerOpportunitiesSection({
  communityGuid,
  currentUserGuid,
  t,
}: {
  communityGuid: string;
  currentUserGuid: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const [roles, setRoles] = useState<ProjectRoleSlotResponse[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * Per-slot state: "none" | "applied" | "applying" | "withdrawing"
   */
  const [slotStates, setSlotStates] = useState<Map<string, string>>(new Map());

  /** Maps slot GUID → assignmentGuid for withdraw calls. */
  const [assignmentMap, setAssignmentMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCommunityOpenRoles(communityGuid)
      .then((data) => {
        if (!cancelled) {
          setRoles(data);
          // Pre-mark slots where the user already has a pending_approval or active assignment
          const states = new Map<string, string>();
          const aMap = new Map<string, string>();
          for (const slot of data) {
            const match = slot.assignments?.find(
              (a) => a.userGuid.toUpperCase() === currentUserGuid.toUpperCase()
                && (a.status === 'active' || a.status === 'pending_approval')
            );
            if (match) {
              const key = slot.roleSlotGuid.toUpperCase();
              states.set(key, 'applied');
              aMap.set(key, match.assignmentGuid);
            }
          }
          setSlotStates(states);
          setAssignmentMap(aMap);
        }
      })
      .catch(() => {
        if (!cancelled) setRoles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [communityGuid, currentUserGuid]);

  // Resolve project names from in-memory store
  const projectNameMap = useMemo(() => {
    const map = new Map<string, string>();
    projectConfigStore.forEach((p) => {
      const guid = String(p.project_guid || p.id || '').toUpperCase();
      if (guid) map.set(guid, String(p.name || p.project_name || ''));
    });
    return map;
  }, []);

  const getSlotState = (slot: ProjectRoleSlotResponse): string => {
    const key = slot.roleSlotGuid.toUpperCase();
    return slotStates.get(key) || 'none';
  };

  const handleApply = async (slot: ProjectRoleSlotResponse) => {
    if (!currentUserGuid) return;
    const key = slot.roleSlotGuid.toUpperCase();
    setSlotStates((prev) => new Map(prev).set(key, 'applying'));
    try {
      const result = await assignUserToRole(slot.projectGuid, slot.roleSlotGuid, {
        userGuid: currentUserGuid,
        assignedBy: null, // self-application → pending_approval
      });
      setSlotStates((prev) => new Map(prev).set(key, 'applied'));
      if (result?.assignmentGuid) {
        setAssignmentMap((prev) => new Map(prev).set(key, result.assignmentGuid));
      }
    } catch (err) {
      console.error('[VolunteerOpportunities] Apply failed:', err);
      setSlotStates((prev) => new Map(prev).set(key, 'none'));
    }
  };

  const handleWithdraw = async (slot: ProjectRoleSlotResponse) => {
    const key = slot.roleSlotGuid.toUpperCase();
    const assignGuid = assignmentMap.get(key);
    if (!assignGuid) {
      // Try to find from API data
      const match = slot.assignments?.find(
        (a) => a.userGuid.toUpperCase() === currentUserGuid.toUpperCase()
          && (a.status === 'active' || a.status === 'pending_approval')
      );
      if (!match) return;
      setAssignmentMap((prev) => new Map(prev).set(key, match.assignmentGuid));
      // Use the match
      await doWithdraw(slot, key, match.assignmentGuid);
      return;
    }
    await doWithdraw(slot, key, assignGuid);
  };

  const doWithdraw = async (slot: ProjectRoleSlotResponse, key: string, assignGuid: string) => {
    setSlotStates((prev) => new Map(prev).set(key, 'withdrawing'));
    try {
      await updateRoleAssignment(slot.projectGuid, slot.roleSlotGuid, assignGuid, {
        status: 'withdrawn',
      });
      setSlotStates((prev) => new Map(prev).set(key, 'withdrawn'));
      setAssignmentMap((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    } catch (err) {
      console.error('[VolunteerOpportunities] Withdraw failed:', err);
      setSlotStates((prev) => new Map(prev).set(key, 'applied')); // revert
    }
  };

  if (loading) {
    return (
      <section className={styles.sectionCard}>
        <h3 className={styles.sectionTitle}>
          <Heart size={15} />
          {t('communityDetail.health.volunteerOpportunities')}
        </h3>
        <p className={styles.sectionHint}>{t('communityDetail.health.volunteerLoading')}</p>
      </section>
    );
  }

  if (roles.length === 0) {
    return (
      <section className={styles.sectionCard}>
        <h3 className={styles.sectionTitle}>
          <Heart size={15} />
          {t('communityDetail.health.volunteerOpportunities')}
        </h3>
        <div className={styles.emptyState}>
          <Heart size={28} />
          <p className={styles.emptyText}>{t('communityDetail.health.volunteerNone')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.sectionCard}>
      <h3 className={styles.sectionTitle}>
        <Heart size={15} />
        {t('communityDetail.health.volunteerOpportunities')}
        <span className={styles.sectionCount}>{roles.length}</span>
      </h3>
      <p className={styles.sectionHint}>{t('communityDetail.health.volunteerOpportunitiesHint')}</p>
      <div className={styles.volunteerGrid}>
        {roles.map((slot) => {
          const remaining = slot.slotsNeeded - slot.slotsFilled;
          const projName = projectNameMap.get(slot.projectGuid.toUpperCase()) || '';
          const state = getSlotState(slot);
          const isBusy = state === 'applying' || state === 'withdrawing';
          return (
            <div key={slot.roleSlotGuid} className={styles.volunteerCard}>
              <div className={styles.volunteerCardHeader}>
                <span className={styles.volunteerLabel}>{slot.roleLabel}</span>
                <span className={styles.volunteerCategoryBadge}>
                  {slot.roleCategory.replace(/_/g, ' ')}
                </span>
              </div>
              {projName && (
                <div className={styles.volunteerProject}>
                  {t('communityDetail.health.volunteerProject')} {projName}
                </div>
              )}
              <div className={styles.volunteerMeta}>
                {slot.timeCommitment && (
                  <span className={styles.volunteerMetaItem}>{slot.timeCommitment}</span>
                )}
                <span className={styles.volunteerMetaItem}>
                  {remaining === 1
                    ? t('communityDetail.health.volunteerSpotLeft')
                    : t('communityDetail.health.volunteerSpotsLeft', { count: remaining })}
                </span>
              </div>
              <div className={styles.volunteerActions}>
                {/* Status chip */}
                {state === 'applied' && (
                  <span className={styles.volunteerAppliedChip}>
                    {t('communityDetail.health.volunteerApplied')} ✓
                  </span>
                )}
                {state === 'withdrawn' && (
                  <span className={styles.volunteerWithdrawnChip}>
                    {t('communityDetail.health.volunteerWithdrawn')}
                  </span>
                )}
                {/* Action button: Apply or Withdraw */}
                {state === 'applied' || state === 'withdrawing' ? (
                  <button
                    type="button"
                    className={styles.volunteerWithdrawBtn}
                    onClick={() => handleWithdraw(slot)}
                    disabled={isBusy}
                  >
                    {state === 'withdrawing'
                      ? t('communityDetail.health.volunteerWithdrawing')
                      : t('communityDetail.health.volunteerWithdraw')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.volunteerBtn}
                    onClick={() => handleApply(slot)}
                    disabled={isBusy}
                  >
                    {state === 'applying'
                      ? t('communityDetail.health.volunteerApplying')
                      : t('communityDetail.health.volunteerBtn')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

const CommunityHealthTab: React.FC<Props> = ({ communityGuid, currentUserGuid }) => {
  const { t } = useAppTranslation('pages');

  const stats = useMemo(() => computeActivityStats(communityGuid), [communityGuid]);
  const enablers = useMemo(() => getEnablersForCommunity(communityGuid), [communityGuid]);

  return (
    <div className={styles.container}>

      {/* ── Activity Summary ── */}
      <section className={styles.sectionCard}>
        <h3 className={styles.sectionTitle}>
          <Activity size={15} />
          {t('communityDetail.health.activitySummary')}
        </h3>
        <div className={styles.statGrid}>
          <StatCard icon={<Users size={16} />} label={t('communityDetail.health.members')} value={stats.memberCount} accent />
          <StatCard icon={<FolderOpen size={16} />} label={t('communityDetail.health.totalProjects')} value={stats.totalProjects} />
          <StatCard icon={<Briefcase size={16} />} label={t('communityDetail.health.activeProjects')} value={stats.activeProjects} accent />
          <StatCard icon={<CheckCircle size={16} />} label={t('communityDetail.health.completedProjects')} value={stats.completedProjects} />
          <StatCard icon={<UserCheck size={16} />} label={t('communityDetail.health.contributors')} value={stats.roleCount} />
          <StatCard icon={<FileEdit size={16} />} label={t('communityDetail.health.draftProjects')} value={stats.draftProjects} />
        </div>
      </section>

      {/* ── Leadership ── */}
      <LeadershipSection enablers={enablers} t={t} />

      {/* ── Open Roles (unfilled community enabler positions) ── */}
      <OpenRolesSection enablers={enablers} t={t} />

      {/* ── Volunteer Opportunities (project open roles) ── */}
      <VolunteerOpportunitiesSection communityGuid={communityGuid} currentUserGuid={currentUserGuid} t={t} />
    </div>
  );
};

export default CommunityHealthTab;
